import React, { useState, useEffect } from "react";
import { X, Search, UserPlus, Gift, Loader2 } from "lucide-react";
import { showToast } from "../../components/common/notification/Notification";
import {
  addPartnerToNetwork,
  searchPartnersByContact,
  triggerPartnerReward,
} from "./networkApi";

const lastTenDigits = (value) => String(value ?? "").replace(/\D/g, "").slice(-10);

const AddPartnerModal = ({ show, onClose, onSuccess, existing = [] }) => {
  const [duplicateError, setDuplicateError] = useState("");
  const [formData, setFormData] = useState({
    first_name: "",
    last_name: "",
    mobile_number: "",
    email_address: "",
    address: "",
    address1: "",
    street: "",
    apartment: "",
    country_name: "India",
    state_name: "",
    city_name: "",
    zip_code: "",
    area_name: "",
  });

  const [searchKeyword, setSearchKeyword] = useState("");
  const [searchResults, setSearchResults] = useState([]);
  const [isSearching, setIsSearching] = useState(false);
  const [isSubmitting, setIsSubmitting] = useState(false);

  // Debounced search for registered partners
  useEffect(() => {
    if (!searchKeyword || searchKeyword.trim().length < 2) {
      setSearchResults([]);
      return;
    }

    const timer = setTimeout(async () => {
      try {
        setIsSearching(true);
        const res = await searchPartnersByContact({
          keyword_search: searchKeyword.trim(),
          user_type: "PARTNER",
        });
        const list = res?.users || res?.partners || [];
        setSearchResults(list);
      } catch (err) {
        console.error("Search partner error:", err);
      } finally {
        setIsSearching(false);
      }
    }, 350);

    return () => clearTimeout(timer);
  }, [searchKeyword]);

  if (!show) return null;

  const handleInputChange = (e) => {
    const { name, value } = e.target;
    setFormData((prev) => ({ ...prev, [name]: value }));
  };

  const handleSelectSuggestedPartner = (user) => {
    setFormData({
      first_name: user.first_name || user.firstName || "",
      last_name: user.last_name || user.lastName || "",
      mobile_number: (user.mobile_number || user.mobileNo || "").replace(/^91/, ""),
      email_address: user.email_address || user.emailId || "",
      address: user.address || "",
      address1: "",
      street: "",
      apartment: "",
      country_name: user.country || "India",
      state_name: user.state || "",
      city_name: user.city || "",
      zip_code: user.zipCode || "",
      area_name: user.area || "",
    });
    setSearchResults([]);
    setSearchKeyword("");
    showToast(`Selected ${user.first_name || "Partner"} from directory`, { type: "info" });
  };

  const handleSubmit = async (e) => {
    e.preventDefault();

    if (!formData.first_name?.trim()) {
      showToast("First Name is required", { type: "error" });
      return;
    }

    if (!formData.mobile_number?.trim() || formData.mobile_number.length < 10) {
      showToast("A valid 10-digit mobile number is required", { type: "error" });
      return;
    }

    const mobile = formData.mobile_number.trim();
    const email = (formData.email_address || "").trim().toLowerCase();
    const isDuplicate = existing.some(
      (p) =>
        lastTenDigits(p.mobile_number || p.mobileNo) === mobile ||
        (email && String(p.email_address || p.emailId || "").toLowerCase() === email)
    );
    if (isDuplicate) {
      setDuplicateError("This partner is already in your network (same mobile number or email).");
      return;
    }
    setDuplicateError("");

    try {
      setIsSubmitting(true);
      const res = await addPartnerToNetwork(formData);

      if (res?.statusType === "SUCCESS" || res?.status === "SUCCESS" || res?.network) {
        // Trigger reward points API
        try {
          await triggerPartnerReward();
        } catch (rewardErr) {
          console.log("Reward hook completed:", rewardErr);
        }

        showToast("Partner successfully added to your network!", { type: "success" });
        onSuccess?.();
        onClose?.();
      } else {
        showToast(res?.text || res?.message || "Failed to add partner", { type: "error" });
      }
    } catch (err) {
      console.error("Add partner error:", err);
      showToast(err?.response?.data?.text || "An error occurred while adding partner", {
        type: "error",
      });
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <div className="net-modal-overlay" onClick={onClose}>
      <div className="net-modal" onClick={(e) => e.stopPropagation()}>
        <form onSubmit={handleSubmit} className="net-modal-form">
          <div className="net-modal__header">
            <div style={{ display: "flex", alignItems: "center", gap: "0.6rem" }}>
              <div
                style={{
                  width: "36px",
                  height: "36px",
                  borderRadius: "10px",
                  background: "#eff6ff",
                  color: "#2563eb",
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "center",
                  flexShrink: 0,
                }}
              >
                <UserPlus size={18} />
              </div>
              <div>
                <h3 className="net-modal__title">Add Network Partner</h3>
                <p style={{ margin: 0, fontSize: "0.75rem", color: "#64748b" }}>
                  Connect an associate partner and earn reward points
                </p>
              </div>
            </div>
            <button type="button" className="net-drawer__close" onClick={onClose}>
              <X size={18} />
            </button>
          </div>

          <div className="net-modal__body">
            {/* Quick Search & Auto-Suggest Box */}
            <div className="net-form-group" style={{ background: "#f8fafc", padding: "0.85rem", borderRadius: "10px", border: "1px solid #e2e8f0" }}>
              <label className="net-form-label" style={{ display: "flex", alignItems: "center", justifyContent: "space-between" }}>
                <span>Search Existing Partner (Optional)</span>
                {isSearching && <Loader2 size={14} className="net-spin" style={{ color: "#2563eb" }} />}
              </label>
              <div style={{ position: "relative" }}>
                <input
                  type="text"
                  className="net-form-input"
                  placeholder="Type name, phone or email to auto-fill..."
                  value={searchKeyword}
                  onChange={(e) => setSearchKeyword(e.target.value)}
                />
              </div>

              {searchResults.length > 0 && (
                <div className="net-suggest-box">
                  {searchResults.map((user) => (
                    <div
                      key={user.id}
                      className="net-suggest-item"
                      onClick={() => handleSelectSuggestedPartner(user)}
                    >
                      <div>
                        <strong style={{ color: "#0f172a" }}>
                          {user.first_name} {user.last_name}
                        </strong>
                        <div style={{ fontSize: "0.75rem", color: "#64748b" }}>
                          {user.mobile_number || user.mobileNo} • {user.email_address || user.emailId || "No email"}
                        </div>
                      </div>
                      <span style={{ fontSize: "0.75rem", color: "#2563eb", fontWeight: 600 }}>Select</span>
                    </div>
                  ))}
                </div>
              )}
            </div>

            <div className="net-form-grid-2">
              <div className="net-form-group">
                <label className="net-form-label">First Name *</label>
                <input
                  type="text"
                  name="first_name"
                  className="net-form-input"
                  placeholder="e.g. Rahul"
                  value={formData.first_name}
                  onChange={handleInputChange}
                  required
                />
              </div>
              <div className="net-form-group">
                <label className="net-form-label">Last Name</label>
                <input
                  type="text"
                  name="last_name"
                  className="net-form-input"
                  placeholder="e.g. Sharma"
                  value={formData.last_name}
                  onChange={handleInputChange}
                />
              </div>
            </div>

            <div className="net-form-grid-2">
              <div className="net-form-group">
                <label className="net-form-label">Mobile Number *</label>
                <input
                  type="tel"
                  name="mobile_number"
                  className="net-form-input"
                  placeholder="10-digit number"
                  value={formData.mobile_number}
                  onChange={handleInputChange}
                  maxLength={10}
                  required
                />
              </div>
              <div className="net-form-group">
                <label className="net-form-label">Email Address</label>
                <input
                  type="email"
                  name="email_address"
                  className="net-form-input"
                  placeholder="partner@example.com"
                  value={formData.email_address}
                  onChange={handleInputChange}
                />
              </div>
            </div>
            {duplicateError && (
              <p className="net-form-error" role="alert" style={{ color: "#dc2626", fontSize: "0.8125rem", margin: "0 0 0.75rem" }}>
                {duplicateError}
              </p>
            )}

            <div className="net-form-grid-2">
              <div className="net-form-group">
                <label className="net-form-label">State Name</label>
                <input
                  type="text"
                  name="state_name"
                  className="net-form-input"
                  placeholder="e.g. Maharashtra"
                  value={formData.state_name}
                  onChange={handleInputChange}
                />
              </div>
              <div className="net-form-group">
                <label className="net-form-label">City Name</label>
                <input
                  type="text"
                  name="city_name"
                  className="net-form-input"
                  placeholder="e.g. Mumbai"
                  value={formData.city_name}
                  onChange={handleInputChange}
                />
              </div>
            </div>

            <div className="net-form-grid-2">
              <div className="net-form-group">
                <label className="net-form-label">Area / Locality</label>
                <input
                  type="text"
                  name="area_name"
                  className="net-form-input"
                  placeholder="e.g. Andheri West"
                  value={formData.area_name}
                  onChange={handleInputChange}
                />
              </div>
              <div className="net-form-group">
                <label className="net-form-label">Zip / Postal Code</label>
                <input
                  type="text"
                  name="zip_code"
                  className="net-form-input"
                  placeholder="e.g. 400053"
                  value={formData.zip_code}
                  onChange={handleInputChange}
                />
              </div>
            </div>

            <div className="net-form-group">
              <label className="net-form-label">Address</label>
              <input
                type="text"
                name="address"
                className="net-form-input"
                placeholder="Building / Street address"
                value={formData.address}
                onChange={handleInputChange}
              />
            </div>

            <div
              style={{
                display: "flex",
                alignItems: "center",
                gap: "0.5rem",
                padding: "0.65rem 0.85rem",
                background: "#fffbeb",
                borderRadius: "8px",
                border: "1px solid #fef3c7",
                color: "#92400e",
                fontSize: "0.75rem",
                fontWeight: 500,
                marginTop: "0.5rem",
              }}
            >
              <Gift size={15} style={{ flexShrink: 0, color: "#d97706" }} />
              <span>Adding this partner will credit bonus reward points to your account.</span>
            </div>
          </div>

          <div className="net-modal__footer">
            <button type="button" className="net-btn net-btn--secondary" onClick={onClose}>
              Cancel
            </button>
            <button
              type="submit"
              className="net-btn net-btn--primary"
              disabled={isSubmitting}
            >
              {isSubmitting ? (
                <>
                  <Loader2 size={16} className="net-spin" /> Adding...
                </>
              ) : (
                "Add Partner to Network"
              )}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
};

export default AddPartnerModal;
